/**
 * Hours booked against a demand's project and role *outside* its plan.
 *
 * A demand's `allocatedHours` only counts bookings the planner created, which
 * carry a `demandId`. A booking made by hand in the planner for the same project
 * and the same role fills the same demand just as truly, so it is rolled up here
 * and folded into the request: the hours left to plan, the hours reported as
 * booked, and the monthly budget the drawer validates against all shrink by it.
 *
 * Bookings that already carry a `demandId` are left alone — they belong to that
 * demand's own server-side figures, and counting them again would double up.
 */

import type { Booking as ApiBooking } from '@/services/api';
import type { BookingStatus } from '@/types';
import type { ResourceRequest } from '@/types/resourceRequest';
import { consumesCapacity } from '@/lib/bookingCapacity';

/** Manual hours a request has, keyed 'yyyy-MM', plus their total. */
export interface ManualBookedHours {
  total: number;
  byMonth: Record<string, number>;
}

export const NO_MANUAL_HOURS: ManualBookedHours = { total: 0, byMonth: {} };

/** Strip case, punctuation and spacing so "Business Analyst" matches "business-analyst". */
function normaliseRole(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function roundHours(hours: number): number {
  return Math.round(hours * 10) / 10;
}

/** The 'yyyy-MM' a booking timestamp falls in, read as the clock time it spells. */
function monthKey(timestamp: string): string | null {
  const parsed = /^(\d{4}-\d{2})/.exec(timestamp);
  return parsed ? parsed[1] : null;
}

function bookingHours(booking: ApiBooking): number {
  if (booking.durationHours > 0) return booking.durationHours;
  const start = new Date(booking.startTime).getTime();
  const end = new Date(booking.endTime).getTime();
  if (isNaN(start) || isNaN(end) || end <= start) return 0;
  return (end - start) / 3_600_000;
}

/**
 * The role a booking is filling: what it was booked for, falling back to the
 * role of the resource doing it (a hand-made booking often names no role).
 */
function bookingRole(
  booking: ApiBooking,
  roleByResourceId: Map<string, string>,
): string | undefined {
  if (booking.requiredRole) return booking.requiredRole;
  return booking.resourceId != null
    ? roleByResourceId.get(String(booking.resourceId))
    : undefined;
}

/**
 * Hours booked by hand for a request's role on its project, per month.
 *
 * `bookings` are the project's bookings (`bookingsApi.getByExternalProjectId`);
 * `roleByResourceId` resolves the role of a booking that names none.
 */
export function manualBookedHoursForRequest(
  bookings: ApiBooking[],
  request: Pick<ResourceRequest, 'id' | 'role'>,
  roleByResourceId: Map<string, string>,
): ManualBookedHours {
  const wantedRole = normaliseRole(request.role);
  if (!wantedRole) return NO_MANUAL_HOURS;

  const byMonth: Record<string, number> = {};
  let total = 0;

  for (const booking of bookings) {
    // The API types a status as free text; capacity only cares about the two
    // statuses that do not consume it, and an unknown one consumes as normal.
    const status = booking.status as BookingStatus;
    if (booking.isDeleted || !consumesCapacity({ status })) continue;
    // Planned bookings are already in the demand's own allocated hours.
    if (booking.demandId != null) continue;
    const role = bookingRole(booking, roleByResourceId);
    if (!role || normaliseRole(role) !== wantedRole) continue;
    const month = monthKey(booking.startTime);
    if (!month) continue;
    const hours = bookingHours(booking);
    if (!(hours > 0)) continue;
    byMonth[month] = roundHours((byMonth[month] ?? 0) + hours);
    total = roundHours(total + hours);
  }

  return { total, byMonth };
}

/**
 * A request with hand-made bookings for its project and role counted in.
 *
 * The hours to still plan for come down, the booked hours go up, and the months
 * carry both — so the drawer plans and validates against what is genuinely left.
 * The status is only ever advanced as far as 'partially-allocated': the demand is
 * still open as far as the source system is concerned, and closing it here would
 * hide a request nobody has actually filled through the planner.
 */
export function applyManualBookedHours(
  request: ResourceRequest,
  manual: ManualBookedHours,
): ResourceRequest {
  if (manual.total <= 0) return request;

  const allocatedHours = roundHours((request.allocatedHours ?? 0) + manual.total);
  const totalHoursRequested = roundHours(Math.max(0, request.totalHoursRequested - manual.total));

  const monthlyAllocatedHours = { ...(request.monthlyAllocatedHours ?? {}) };
  const monthlyHourHints = { ...(request.monthlyHourHints ?? {}) };
  for (const [month, hours] of Object.entries(manual.byMonth)) {
    monthlyAllocatedHours[month] = roundHours((monthlyAllocatedHours[month] ?? 0) + hours);
    if (month in monthlyHourHints) {
      const left = roundHours(Math.max(0, monthlyHourHints[month] - hours));
      if (left > 0) monthlyHourHints[month] = left;
      else delete monthlyHourHints[month];
    }
  }

  const closed = request.status === 'accepted' || request.status === 'declined';

  return {
    ...request,
    totalHoursRequested,
    allocatedHours,
    originalHoursRequested: request.originalHoursRequested
      ?? roundHours(request.totalHoursRequested + (request.allocatedHours ?? 0)),
    monthlyAllocatedHours,
    monthlyHourHints: Object.keys(monthlyHourHints).length > 0 ? monthlyHourHints : undefined,
    manualBookedHours: manual.total,
    status: closed ? request.status : 'partially-allocated',
  };
}
